import {FeatureFlag} from "@services/featureFlagConstants";
import {fetchFeatureFlags} from "@services/featureFlags";
import {NextPage} from "next";
import {useRouter} from "next/router";
import {useTranslation} from "ni18n";
import React, {useEffect} from "react";
import {useDispatch} from "react-redux";
import Notice from "src/components/v5/Maps/Notice";
import OtherProviders from "src/components/v5/OtherProviders";
import useLocationConstants, {isFullPageBanner} from "src/constants/LocationConstants";
import {isAlaskaAirlinesLoc, isPopupLoc, popupLocationsFilter} from "src/constants/locations";
import {RootStateApptReason, RootStateLocation, RootStatePracticeSpecialty} from "src/store/types";
import {v4} from "uuid";

import {api} from "../../_services/api";
import {forwardTo, v5Pages} from "../../components/_common/_constants";
import {ands} from "../../components/_common/Carbon";
import Layout from "../../components/v5/Layout";
import Main from "../../components/v5/Location/Main";
import Photos from "../../components/v5/Location/Photos";
import TestimonialSlider from "../../components/v5/TestimonialSlider";
import {actions, useTypedSelector} from "../../store";
import {selectSelectedRegion} from "../../store/slices/userLocation";
import {NextPageContextWithStore} from "../../store/types";
import {s3ImageSource} from "../../useS3ImgSrc";
import {isCI} from "../../utils/env";
import {getLocationSchemas} from "../../utils/schemaUtils";

type Props = {
  location: RootStateLocation;
  flowId: string;
  popularReasons: RootStateApptReason[] | [];
  isTravelClearance?: boolean;
};

// @ts-expect-error TS2322: Type '{ ({ flowId, location: _location, popularReasons, isTravelClearance, }: PropsWithChildren<Props>): Element; getInitialProps({ query, store, res, req, asPath, }: NextPageContextWithStore): Promise<{ location: RootStateLocation | undefined; popularReasons: any; isTravelClearance: boolean | undefined; flowId: string; }...' is not assignable to type 'NextPage<Props, Props>'.
const ClinicDetails: NextPage<Props> = ({location: _location, popularReasons}) => {
  const dispatch = useDispatch();
  const {query} = useRouter();
  const {slug} = query;

  const selectedRegion = useTypedSelector(selectSelectedRegion);

  const {
    locationsSorted,
    locations = [],
    practices = [],
    imgExt,
    isMobile,
    title,
    description,
  } = useTypedSelector(({config, metadata}) => ({...config, ...metadata}));
  const location = _location || locations.find(l => l.slug === slug);

  const i18n = useTranslation();
  const locationsExcludePopups = locations.filter(popupLocationsFilter);
  const isPopup = isPopupLoc(location.name);
  const isAlaska = isAlaskaAirlinesLoc(location.id);
  const {longitude, latitude} = location.address;

  const practice = practices.find(p => p.id === location?.practiceId);
  const {images, name, specialties = {}} = location;

  const showImages = (images || []).length > 1;
  const props = {
    slug,
    location,
    locationsSorted,
    locations: locationsExcludePopups,
    practices,
    practice,
    imgExt,
    showImages,
    isMobile,
    isPopup,
  };

  useEffect(() => {
    const selectedLocationsRegion = location.region.slug;
    if (selectedRegion !== selectedLocationsRegion) {
      dispatch(actions.setUserLocation({selectedRegion: selectedLocationsRegion}));
    }
  }, [location.region.slug, selectedRegion]);

  const pageTitle = specialties
    ? `${name || slug} | ${
        ands(
          i18n.lang,
          specialties.toArray<RootStatePracticeSpecialty>().map(s => s[1].name),
        ) || ""
      }`
    : "Carbon Health";

  const pageDescription = specialties
    ? `${ands(
        i18n.lang,
        specialties.toArray<RootStatePracticeSpecialty>().map(s => s[1].name),
      )} | ${name}`
    : "Carbon Health";

  const ogImage = s3ImageSource(((images || [])[0] || {}).imageId, "jpg");

  // @ts-expect-error TS7006: Parameter 'd' implicitly has an 'any' type.
  const doctorFilter = d =>
    d.specialties.includes("Primary Care") && d.primaryLocationId === location.id;

  const bannerProps = useLocationConstants({locationId: location.id})?.banners.filter(b =>
    isFullPageBanner(b),
  )[0];

  const schemas = getLocationSchemas(location, ogImage);

  return (
    <Layout
      noIndex={isAlaska}
      style={{backgroundColor: "#fff"}}
      pageTitle={title || pageTitle}
      pageDescription={description || pageDescription}
      className={!showImages ? "noImages" : ""}
      absolutePageImage={ogImage}
      navStartTransparent={false}
      schemas={schemas}
      blockChatWidget={isMobile}
      wrapClasses="ovf-unset"
    >
      <div className="minh18 minh14-sm" />
      {bannerProps && <Notice {...bannerProps} className="br0-f w100p-f jcc-f pt6-f pb4-f" />}
      {showImages && <Photos {...{location}} />}
      <Main {...props} popularReasons={popularReasons} />
      <OtherProviders
        filter={doctorFilter}
        tag={location.name}
        header={i18n.t("Meet our {{clinic}} providers.", {clinic: location.name})}
        coordinate={{longitude, latitude}}
        radius={0.1}
      />
      <TestimonialSlider />
    </Layout>
  );
};

// @ts-expect-error TS2322: Type '({ query, store, res, req, asPath, }: NextPageContextWithStore) => Promise<{ location: RootStateLocation | undefined; popularReasons: any; isTravelClearance: boolean | undefined; flowId: string; }>' is not assignable to type '(context: NextPageContext<any>) => Props | Promise<Props>'.
ClinicDetails.getInitialProps = async ({
  query,
  store,
  res,
  req,
  pathname,
}: NextPageContextWithStore) => {
  const slug = query.slug as string;

  const {
    config: {locations: reduxLocations},
  } = store?.getState() || {};

  const locations =
    reduxLocations ||
    // @ts-expect-error TS2352: Conversion of type '({ id: string; practiceId: string; typ: string; coverImageId: string; name: string; phoneNumber: string; slug: string; address: { firstLine: string; city: string; state: string; zip: string; latitude: number; ... 4 more ...; secondLine?: undefined; }; ... 9 more ...; deactivatedAt?: undefined; } | ... 21 more ... | ...' to type 'RootStateLocation[]' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
    ((await (
      await import("../../../public/static/data/localLocations.json")
    ).default) as RootStateLocation[]);

  const location = locations.find(l => l.slug === slug);
  if (!location) {
    await forwardTo(res, v5Pages.locations);
  }

  let popularReasons = [];

  if (location) {
    const url = `/hib/locations/${location.id}/popularApptReasons`;
    popularReasons = isCI
      ? (await import("../../../cypress/fixtures/popularReasons")).default
      : await api.get(url);
  }

  await fetchFeatureFlags(
    {
      [FeatureFlag.CARE_DISCOVERY_TOPICS_SEARCH_ENABLED]: true,
      [FeatureFlag.GROWTH_DISCOVERY_SEARCH_AVAILABILITY_ENABLED]: true,
    },
    {
      dispatch: store.dispatch,
      req,
      query,
      overrideAttribute: {pathname},
    },
  );

  return {location, popularReasons, flowId: v4()};
};

export default ClinicDetails;
